import {Component, inject, signal} from "@angular/core";
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from "@angular/forms";
import {MatFormFieldModule} from "@angular/material/form-field";
import {MatInputModule} from "@angular/material/input";
import {MatButtonModule} from "@angular/material/button";
import {MatBottomSheetRef} from "@angular/material/bottom-sheet";
import {CoreService} from "../../../../../../shared/src/lib/services/core.service";
import {ApiService} from "../../../../../../shared/src";
import {SnackbarService} from "../../../../../../shared/src/lib/services/snackbar.service";
import {MatProgressBarModule} from "@angular/material/progress-bar";
import {TranslateModule} from "@ngx-translate/core";
import {LoaderService} from "../../../../../../shared/src/lib/services/loader.service";
import {strongPasswordPattern} from "../../../../../../shared/src/lib/utils/common";
import {PasswordValidationStepComponent} from "./components/password-validation-step/password-validation-step.component";
import {strongPasswordValidator} from "./validators/strong-password.validator";
import {confirmPasswordValidator} from "./validators/confirm-password.validator";
import {MatIcon} from "@angular/material/icon";

@Component({
    selector: "app-change-password",
    imports: [MatFormFieldModule, MatInputModule, MatButtonModule, ReactiveFormsModule, MatProgressBarModule, TranslateModule, PasswordValidationStepComponent, MatIcon],
    templateUrl: "./change-password.component.html",
    styleUrl: "./change-password.component.scss"
})
export class ChangePasswordComponent {
    coreService = inject(CoreService);
    apiService = inject(ApiService);
    snackbar = inject(SnackbarService);
    form: FormGroup;
    loader = inject(LoaderService);
    strongPassword = strongPasswordPattern;
    showPassword = signal<boolean>(false);
    protected readonly Boolean = Boolean;

    constructor(
        private fb: FormBuilder,
        public bottomSheetRef: MatBottomSheetRef,
    ) {
        this.form = this.fb.group({
            PASSWORD: ["", [Validators.required]],
            NEW_PASSWORD: ["", [Validators.required, strongPasswordValidator]],
            CONFIRM_PASSWORD: ["", [Validators.required]],
        }, {validators: [confirmPasswordValidator]});
    }

    get oldPassword() {
        return this.form.get("PASSWORD");
    }

    get newPassword() {
        return this.form.get("NEW_PASSWORD");
    }

    get confirmPassword() {
        return this.form.get("CONFIRM_PASSWORD");
    }

    handleSubmit() {
        if (this.form.valid) {
            const authToken = this.apiService.getUserToken();
            const userName = authToken?.user?.NAME;

            if (!userName) {
                console.error("No username found.");
                return;
            }

            const data = {
                USERNAME: userName,
                PASSWORD: this.form.value.PASSWORD,
                NEW_PASSWORD: this.form.value.NEW_PASSWORD,
            };

            this.coreService.changePassword(data).subscribe({
                next: (response: any) => {
                    console.log(response);
                    if (response?.status === "ok") {
                        this.snackbar.openSnackbar({
                            message: "Password changed successfully!",
                        });
                        this.form.reset();
                        this.bottomSheetRef.dismiss("changed");
                    } else {
                        this.snackbar.openSnackbar({
                            message: "Unable to change password: the current password provided is incorrect.",
                        });
                    }
                },
                error: (err) => {
                    console.error(err);
                },
            });
        } else {
            console.error("Form is not valid:", this.form.errors);
        }
    }

    generateRandomPassword(): void {
        const upper = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
        const lower = 'abcdefghijklmnopqrstuvwxyz'
        const numbers = '0123456789'
        const specials = '!@#$%^&*(),.?":{}|<>'

        function getRandomChar(chars: string): string {
            return chars[Math.floor(Math.random() * chars.length)]
        }

        const password = [
            getRandomChar(upper),
            getRandomChar(lower),
            getRandomChar(numbers),
            getRandomChar(numbers),
            getRandomChar(numbers),
            getRandomChar(specials),
        ]

        // Shuffle the password array
        for (let i = password.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1))
            ;[password[i], password[j]] = [password[j], password[i]]
        }

        const shuffledPassword = password.join('')

        this.newPassword?.setValue(shuffledPassword, {emitEvent: true})
        this.newPassword?.markAsDirty()
    }

    togglePasswordVisibility() {
        this.showPassword.update((currentValue) => !currentValue)
    }
}
